import { ReactNode } from 'react';
import { Icon } from './Icon';
import { KeyDisplay } from './KeyDisplay';
import { ActionForContext } from '../../utils/controls.utils';
import { GameplayContext } from '../../data/controls.config';
import { useActionPulse } from '../../store/actionPulseSlice';
import './IconWithKey.scss';

interface Props<C extends GameplayContext> {
  sprite?: string;
  context: C;
  action: ActionForContext<C>;
  size?: number;
  cooldownPct?: number;
  className?: string;
  variant?: 'default' | 'basic';
  title?: string;
  children?: ReactNode;
}

export const IconWithKey = <C extends GameplayContext>({
  sprite,
  context,
  action,
  size = 48,
  cooldownPct,
  className,
  variant = 'default',
  title,
  children,
}: Props<C>) => {
  const slotPad = 2;
  const slotSize = size + slotPad * 2;
  // Counter incremented on each action press — used as the `key` of the
  // animation wrapper to restart the `icon-bump` keyframe on every pulse, even
  // if the previous animation is still running.
  const pulse = useActionPulse(action as string);

  return (
    <div
      className={['icon-with-key', `icon-with-key--${variant}`, className ?? '']
        .filter(Boolean)
        .join(' ')}
      style={{ width: slotSize, height: slotSize }}
      title={title}
    >
      <div
        className="icon-with-key-bump"
        // `key` changes on each pulse → remount → animation replayed. On the
        // initial mount (pulse = 0) `data-bumped="0"` disables the animation.
        key={pulse > 0 ? pulse : 'init'}
        data-bumped={pulse > 0 ? '1' : '0'}
      >
        <Icon sprite={sprite} size={size} />
      </div>
      {cooldownPct !== undefined && cooldownPct > 0 && (
        <div
          className="icon-with-key-cooldown"
          style={
            {
              '--cd-pct': `${cooldownPct * 100}%`,
            } as React.CSSProperties
          }
        />
      )}
      {children}
      <KeyDisplay
        context={context}
        action={action}
        size="sm"
        className="icon-with-key-key"
      />
    </div>
  );
};
